"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import posthog from "posthog-js";
import { Box, Button, Container, Skeleton, Snackbar, Alert, IconButton } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { DEFAULT_OG_IMAGE_PATH } from "@/app/_utils/constants";
import NotFoundSection from "@/app/components/NotFoundSection";
import { useCart } from "@/app/_utils/providers/CartProvider";
import PublicFestivalService, {
  PublicFestival,
  PublicFestivalDetail,
  PublicFestivalShow,
} from "@/app/_services/PublicFestivalService";
import { FestivalPageHelmet } from "@/app/(public)/_seo/FestivalPageHelmet";
import FoodTourBooking from "@/app/(public)/festivals/FoodTourBooking";
import {
  ShopDrawing,
  Walker,
  TOUR_CREAM,
  TOUR_GREEN,
  TOUR_INK,
  TOUR_POP,
  TourFonts,
  characterFor,
  tourBodyFont,
  tourDisplayFont,
} from "@/app/(public)/festivals/FoodTourArt";
import { dayjsShow } from "@/app/_utils/dayjs";
import { HELD_LABEL, showAvailability } from "@/app/_utils/availability";
import { CartError } from "@/app/_services/CartService";
import { trackWaitlistClick } from "@/app/_utils/waitlistAnalytics";
import {
  PUBLIC_PAGE_CONTAINER_MAX_WIDTH,
  publicPageContainerSx,
} from "@/app/_utils/publicLayout";

const pageFont =
  '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const pageBg = "#0a0a0a";
const accent = "#FF5147";
const textPrimary = "#ffffff";
const textMuted = "#999999";
const hairline = "rgba(255,255,255,0.12)";
const cardBg = "#141414";
const cardBorder = "rgba(255,255,255,0.10)";
const placeholderImg = DEFAULT_OG_IMAGE_PATH;

const reducedMotionSx = {
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none !important",
    transform: "none !important",
  },
} as const;

type Availability = "sold-out" | "held" | "few-left" | "available";
type ViewMode = "rows" | "grid";

function availabilityFor(show: PublicFestivalShow): Availability {
  const inv = show.inventory_quantity;
  const state = showAvailability(show);
  if (state === "held") return "held";
  if (state === "sold_out") return "sold-out";
  if (show.capacity > 0 && inv != null) {
    const sold = show.capacity - inv - (show.held_quantity ?? 0);
    if (sold / show.capacity >= 0.85) return "few-left";
  }
  return "available";
}

function statusFor(show: PublicFestivalShow): { label: string; color: string } {
  const availability = availabilityFor(show);
  if (availability === "sold-out") return { label: "Sold out", color: accent };
  if (availability === "held") return { label: HELD_LABEL, color: "#fcd34d" };
  const left = show.inventory_quantity;
  if (availability === "few-left" && left != null && left > 0) {
    return { label: `${left} left`, color: accent };
  }
  if (availability === "few-left") return { label: "Few left", color: accent };
  return { label: "Available", color: "#5dcaa5" };
}

function dayKey(start: string): string {
  return dayjsShow(start).format("YYYY-MM-DD");
}

function formatDateRange(shows: PublicFestivalShow[]): string {
  if (shows.length === 0) return "";
  const sorted = [...shows].sort(
    (a, b) => dayjsShow(a.start).valueOf() - dayjsShow(b.start).valueOf()
  );
  const first = dayjsShow(sorted[0].start);
  const last = dayjsShow(sorted[sorted.length - 1].start);
  if (first.isSame(last, "day")) return first.format("MMM D, YYYY");
  if (first.isSame(last, "month")) return `${first.format("MMM D")}–${last.format("D, YYYY")}`;
  if (first.isSame(last, "year")) return `${first.format("MMM D")} – ${last.format("MMM D, YYYY")}`;
  return `${first.format("MMM D, YYYY")} – ${last.format("MMM D, YYYY")}`;
}

const FOOD_TOUR_VENUES = ["zabar", "veselka", "russ", "economy", "greengrass", "barney"];

function isSmallBusinessTour(
  festival: PublicFestival | undefined,
  shows: PublicFestivalShow[] = [],
): boolean {
  const hay = `${festival?.slug ?? ""} ${festival?.title ?? ""} ${festival?.description ?? ""}`.toLowerCase();
  if (
    hay.includes("business") ||
    hay.includes("food") ||
    hay.includes("neighborhood") ||
    hay.includes("legend")
  ) {
    return true;
  }
  const venues = shows
    .map((show) => `${show.location?.name ?? ""} ${show.title ?? ""}`.toLowerCase())
    .join(" ");
  return FOOD_TOUR_VENUES.some((name) => venues.includes(name));
}

const SAMPLE_FESTIVAL: PublicFestivalDetail = {
  preview: true,
    stats: { nights: 5, venues: 5, comedians: 0 },
  festival: {
    id: "sample",
    title: "Small Business Comedy Tour",
    slug: "preview",
    description: "Comedy popping up inside the businesses that make New York, New York.",
    img_url: "https://storage.googleapis.com/cuo-website/Chris%20Redd%20FT.png",
    published: false,
  },
  shows: [
    ["2027-01-10T19:30:00", "Barney Greengrass", "https://storage.googleapis.com/cuo-website/Mark%20Barney%20Green%20(Small).jpg", 40, 6, 0],
    ["2027-01-12T19:30:00", "Veselka", "https://storage.googleapis.com/cuo-website/Dan+Soder+at+Veselka.jpg", 90, 40, 0],
    ["2027-01-13T19:30:00", "Russ & Daughters", "https://storage.googleapis.com/cuo-website/Jared%20FT.png", 180, 180, 0],
    ["2027-01-13T21:45:00", "Russ & Daughters", "https://storage.googleapis.com/cuo-website/Jared%20FT.png", 180, 180, 0],
    ["2027-01-14T19:30:00", "Zabar's", "https://storage.googleapis.com/cuo-website/Dave+Attell+at+Zabar's.jpg", 110, 110, 0],
    ["2027-01-14T21:45:00", "Zabar's", "https://storage.googleapis.com/cuo-website/Dave+Attell+at+Zabar's.jpg", 110, 110, 0],
    ["2027-01-15T19:30:00", "Economy Candy", "https://storage.googleapis.com/cuo-website/Chris+Redd+at+Economy+Candy.jpg", 110, 20, 0],
    ["2027-01-15T21:45:00", "Economy Candy", "https://storage.googleapis.com/cuo-website/Chris+Redd+at+Economy+Candy.jpg", 110, 20, 0],
  ].map(([start, name, img, capacity, inventory, held], index) => ({
    id: `sample-${index}`,
    title: String(name),
    slug: "",
    start: String(start),
    img_url: String(img),
    ticket_price: 45,
    capacity: Number(capacity),
    inventory_quantity: Number(inventory),
    held_quantity: Number(held),
    max_quantity_per_order: 4,
    location: { name: String(name), city: "New York" },
  })),
};

function scrollToId(id: string) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(id)?.scrollIntoView({
    behavior: reduce ? "auto" : "smooth",
    block: "start",
  });
}

function FestivalPageSkeleton() {
  return (
    <>
      <Header />
      <Box sx={{ bgcolor: pageBg, minHeight: "80vh", fontFamily: pageFont }}>
        <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={publicPageContainerSx}>
          <Box sx={{ pt: 8, pb: 6 }}>
            <Skeleton variant="text" width={220} height={24} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
            <Skeleton variant="text" width="70%" height={80} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
            <Skeleton variant="text" width="46%" height={80} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
          </Box>
        </Container>
      </Box>
      <Footer />
    </>
  );
}

function chipButtonSx(active: boolean) {
  return {
    appearance: "none",
    fontFamily: pageFont,
    cursor: "pointer",
    borderRadius: 999,
    px: 1.75,
    py: 0.9,
    border: `1px solid ${active ? "#fff" : hairline}`,
    bgcolor: active ? "#fff" : "transparent",
    color: active ? "#000" : textPrimary,
    whiteSpace: "nowrap",
    flex: "none",
    ...reducedMotionSx,
  } as const;
}

export default function FestivalSlugPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get("preview");
  const samplePreview = slug === "preview";
  const { cart, addToCart, updateItem, removeItem } = useCart();

  const [data, setData] = useState<PublicFestivalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<string>("all");
  const [view, setView] = useState<ViewMode>("rows");
  const [pendingShowId, setPendingShowId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});
  const [sampleQty, setSampleQty] = useState<Record<string, number>>({});
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setSelectedDay(null);
    setSelectedVenue("all");
    if (samplePreview) {
      setData(SAMPLE_FESTIVAL);
      setLoading(false);
      return;
    }
    PublicFestivalService.getFestivalBySlug(slug, previewToken)
      .then((result) => setData(result))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug, previewToken, samplePreview]);

  const isPreview = !!data?.preview;
  const festival = data?.festival;
  const shows = data?.shows ?? [];

  const days = useMemo(() => {
    const seen = new Map<string, string>();
    for (const show of shows) {
      const key = dayKey(show.start);
      if (!seen.has(key)) seen.set(key, show.start);
    }
    return Array.from(seen.entries())
      .sort(([, a], [, b]) => dayjsShow(a).valueOf() - dayjsShow(b).valueOf())
      .map(([key, start]) => ({
        key,
        weekday: dayjsShow(start).format("ddd"),
        date: dayjsShow(start).format("MMM D"),
      }));
  }, [shows]);

  const venues = useMemo(() => {
    const names = new Set<string>();
    for (const show of shows) {
      if (show.location?.name) names.add(show.location.name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [shows]);

  const venueCards = useMemo(() => {
    const byName = new Map<string, PublicFestivalShow>();
    for (const show of shows) {
      const name = show.location?.name;
      if (!name || byName.has(name)) continue;
      byName.set(name, show);
    }
    return Array.from(byName.entries()).map(([name, show]) => ({ name, show }));
  }, [shows]);

  const visibleShows = useMemo(() => {
    return shows
      .filter((show) => (selectedDay ? dayKey(show.start) === selectedDay : true))
      .filter((show) => (selectedVenue === "all" ? true : show.location?.name === selectedVenue))
      .sort((a, b) => dayjsShow(a.start).valueOf() - dayjsShow(b.start).valueOf());
  }, [shows, selectedDay, selectedVenue]);

  const dayGroups = useMemo(() => {
    const groups: { key: string; label: string; shows: PublicFestivalShow[] }[] = [];
    for (const show of visibleShows) {
      const key = dayKey(show.start);
      const last = groups[groups.length - 1];
      if (!last || last.key !== key) {
        groups.push({
          key,
          label: dayjsShow(show.start).format("dddd, MMMM D"),
          shows: [show],
        });
      } else {
        last.shows.push(show);
      }
    }
    return groups;
  }, [visibleShows]);

  const festivalShowIds = useMemo(() => new Set(shows.map((s) => s.id)), [shows]);
  const festivalCartItems = useMemo(() => {
    if (samplePreview) {
      return shows
        .filter((show) => (sampleQty[show.id] ?? 0) > 0)
        .map((show) => ({
          id: show.id,
          quantity: sampleQty[show.id],
          total_price: show.ticket_price * sampleQty[show.id],
          show: { id: show.id, title: show.title },
        }));
    }
    return (cart?.items ?? []).filter((item) => !item.merch && item.show?.id && festivalShowIds.has(item.show.id));
  }, [samplePreview, sampleQty, shows, cart, festivalShowIds]);
  const passTotal = festivalCartItems.reduce((sum, item) => sum + (item.total_price ?? 0), 0);

  function maxQuantityFor(show: PublicFestivalShow): number {
    const caps = [show.max_quantity_per_order];
    if (show.inventory_quantity != null) caps.push(show.inventory_quantity);
    return Math.max(0, Math.min(...caps));
  }

  function quantityFor(show: PublicFestivalShow): number {
    if (samplePreview) return sampleQty[show.id] ?? 0;
    if (show.id in localQuantities) return localQuantities[show.id];
    return festivalCartItems.find((i) => i.show?.id === show.id)?.quantity ?? 0;
  }

  async function commitQuantity(show: PublicFestivalShow, quantity: number) {
    setPendingShowId(show.id);
    try {
      const inCartItem = festivalCartItems.find((i) => i.show?.id === show.id);
      const previousQuantity = inCartItem?.quantity ?? 0;
      if (quantity === 0) {
        if (inCartItem) await removeItem(inCartItem.id);
      } else if (!inCartItem) {
        await addToCart(show.id, quantity);
      } else if (inCartItem.quantity !== quantity) {
        await updateItem(inCartItem.id, quantity);
      }

      const addedQuantity = quantity - previousQuantity;
      if (addedQuantity > 0) {
        posthog.capture("add_to_cart", {
          show_id: show.id,
          show_name: show.title,
          show_date: show.start ?? undefined,
          quantity: addedQuantity,
          price: show.ticket_price,
          festival_slug: data?.festival?.slug,
          festival_title: data?.festival?.title,
        });
      }
    } catch (e) {
      setErrorMessage(
        e instanceof CartError && e.code === "held"
          ? e.message
          : "Could not update your pass. Please try again."
      );
    } finally {
      setPendingShowId(null);
      setLocalQuantities((prev) => {
        const next = { ...prev };
        delete next[show.id];
        return next;
      });
    }
  }

  function adjustQuantity(show: PublicFestivalShow, delta: number) {
    if (samplePreview) {
      const next = Math.max(0, Math.min(maxQuantityFor(show), (sampleQty[show.id] ?? 0) + delta));
      setSampleQty((prev) => ({ ...prev, [show.id]: next }));
      return;
    }
    if (isPreview) return;
    const next = Math.max(0, Math.min(maxQuantityFor(show), quantityFor(show) + delta));
    setLocalQuantities((prev) => ({ ...prev, [show.id]: next }));

    if (debounceTimers.current[show.id]) {
      clearTimeout(debounceTimers.current[show.id]);
    }
    debounceTimers.current[show.id] = setTimeout(() => {
      commitQuantity(show, next);
    }, 500);
  }

  function trackFilter(filter: string, value: string) {
    posthog.capture("festival_filter", {
      festival_slug: festival?.slug,
      filter,
      value,
    });
  }

  if (loading) return <FestivalPageSkeleton />;

  if (!data || !festival) {
    return (
      <>
        <FestivalPageHelmet slug={slug} title="Festival" noIndex />
        <NotFoundSection
          title="Festival not found"
          message="This festival doesn't exist or is no longer available."
          ctaLabel="Back to Tickets"
          ctaHref="/tickets"
        />
      </>
    );
  }

  const dateRangeLabel = formatDateRange(shows);
  const cities = Array.from(new Set(shows.map((show) => show.location?.city).filter(Boolean))) as string[];
  const cityLabel = cities.length === 1 ? cities[0].toUpperCase() : "";
  const fromPrice = shows.reduce((min, show) => Math.min(min, show.ticket_price), Number.POSITIVE_INFINITY);
  const fromPriceLabel = Number.isFinite(fromPrice) ? `From $${Math.round(fromPrice)}` : "";
  const nightCount = new Set(shows.map((show) => dayKey(show.start))).size;
  const tourCopy = isSmallBusinessTour(festival, shows);

  function chipSx(active: boolean) {
    if (!tourCopy) return chipButtonSx(active);
    return {
      appearance: "none" as const,
      fontFamily: tourBodyFont,
      fontWeight: 800,
      cursor: "pointer",
      borderRadius: 999,
      px: 1.75,
      py: 0.9,
      border: `2px solid ${TOUR_INK}`,
      bgcolor: active ? TOUR_INK : TOUR_CREAM,
      color: active ? TOUR_CREAM : TOUR_INK,
      boxShadow: active ? "none" : `2px 2px 0 ${TOUR_INK}`,
      whiteSpace: "nowrap" as const,
      flex: "none",
      ...reducedMotionSx,
    };
  }

  function openShow(show: PublicFestivalShow) {
    posthog.capture("festival_show_open", {
      show_id: show.id,
      show_slug: show.slug,
      festival_slug: festival?.slug,
    });
  }

  function renderAddControl(show: PublicFestivalShow) {
    const availability = availabilityFor(show);
    const quantity = quantityFor(show);
    const isPending = pendingShowId === show.id;
    const blocked = availability === "sold-out" || availability === "held";
    if (blocked) {
      return (
        <Button
          disabled
          variant="contained"
          disableElevation
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            fontFamily: pageFont,
            bgcolor: "rgba(255,255,255,0.08) !important",
            color: "rgba(255,255,255,0.55) !important",
            boxShadow: "none",
          }}
        >
          {availability === "held" ? HELD_LABEL : "Sold out"}
        </Button>
      );
    }
    if (quantity === 0) {
      return (
        <Button
          variant="contained"
          disableElevation
          disabled={isPending || (isPreview && !samplePreview)}
          onClick={() => adjustQuantity(show, 1)}
          aria-label={`Add ${show.title}`}
          sx={{
            bgcolor: "#fff",
            color: "#000",
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            fontFamily: pageFont,
            px: 2.25,
            "&:hover": { bgcolor: "#eee" },
          }}
        >
          + Add show
        </Button>
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          border: `1px solid ${hairline}`,
          borderRadius: 999,
          pl: 0.5,
          pr: 0.5,
          bgcolor: "transparent",
        }}
      >
        <IconButton
          size="small"
          disabled={isPending}
          aria-label={`Remove one ticket for ${show.title}`}
          onClick={() => adjustQuantity(show, -1)}
          sx={{ color: textPrimary }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 18, textAlign: "center", color: textPrimary, fontWeight: 700, fontSize: 14 }}>
          {isPending ? (
            <Skeleton variant="text" width={12} sx={{ bgcolor: "rgba(255,255,255,0.15)", mx: "auto" }} />
          ) : (
            quantity
          )}
        </Box>
        <IconButton
          size="small"
          disabled={isPending || quantity >= maxQuantityFor(show)}
          aria-label={`Add one ticket for ${show.title}`}
          onClick={() => adjustQuantity(show, 1)}
          sx={{ color: textPrimary }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  function renderShowRow(show: PublicFestivalShow) {
    const status = statusFor(show);
    const when = dayjsShow(show.start);
    const cast = tourCopy ? characterFor(`${show.location?.name ?? ""} ${show.title}`) : null;
    if (tourCopy) {
      const picked = quantityFor(show) > 0;
      const place = [show.location?.name, show.location?.city].filter(Boolean).join(", ");
      return (
        <Box
          key={show.id}
          id={`show-${show.id}`}
          sx={{
            scrollMarginTop: 96,
            position: "relative",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { sm: "center" },
            gap: { xs: 1.5, sm: 2 },
            bgcolor: "#121212",
            border: `1px solid ${picked ? "rgba(255,81,71,0.7)" : cardBorder}`,
            borderRadius: "18px",
            mt: 8,
            px: { xs: 1.5, md: 2 },
            py: 1.5,
            overflow: "visible",
            ...reducedMotionSx,
          }}
        >
          {cast && <Walker id={cast} celebrate={picked} />}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Box sx={{ width: 52, flex: "none", textAlign: "center" }}>
              <Box sx={{ fontSize: 11, letterSpacing: 1, color: textMuted, fontWeight: 700 }}>{when.format("ddd").toUpperCase()}</Box>
              <Box sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{when.format("D")}</Box>
              <Box sx={{ fontSize: 11, letterSpacing: 1, color: textMuted, fontWeight: 700 }}>{when.format("MMM").toUpperCase()}</Box>
            </Box>
            <Box sx={{ width: { xs: 92, sm: 112 }, height: 76, flex: "none", borderRadius: "10px", overflow: "hidden", bgcolor: "#000" }}>
              <Box
                component="img"
                src={show.img_url || placeholderImg}
                alt={show.location?.name || show.title}
                loading="lazy"
                decoding="async"
                sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ color: textMuted, fontSize: 13 }}>
              {when.format("h:mm A")}
              {place ? ` · ${place}` : ""}
            </Box>
            <Box sx={{ fontWeight: 800, fontSize: { xs: 18, md: 22 }, mt: 0.35, lineHeight: 1.15 }}>{show.title}</Box>
            {show.slug && (
              <Box
                component={RouterLink}
                to={`/products/${show.slug}`}
                onClick={() => openShow(show)}
                sx={{ display: "inline-block", mt: 0.6, color: accent, fontWeight: 700, fontSize: 14, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                Show details
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flex: "none", minWidth: { sm: 180 } }}>
            <Box sx={{ textAlign: { sm: "right" } }}>
              <Box sx={{ fontWeight: 800, fontSize: 22 }}>${Math.round(show.ticket_price)}</Box>
              <Box sx={{ color: status.color, fontSize: 13, fontWeight: 700 }}>{status.label}</Box>
            </Box>
            {renderAddControl(show)}
          </Box>
        </Box>
      );
    }
    return (
      <Box
        key={show.id}
        id={`show-${show.id}`}
        sx={{
          scrollMarginTop: 88,
          bgcolor: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: "14px",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 2 },
          p: { xs: 1.5, sm: 1.75 },
          alignItems: { sm: "center" },
          transition: "border-color 180ms ease",
          "&:hover": { borderColor: "rgba(255,255,255,0.28)" },
          ...reducedMotionSx,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", minWidth: 0 }}>
          <Box sx={{ width: 52, flex: "none", textAlign: "center", color: textPrimary }}>
            <Box sx={{ fontSize: 11, letterSpacing: 1, color: textMuted, fontWeight: 700 }}>
              {when.format("ddd").toUpperCase()}
            </Box>
            <Box sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{when.format("D")}</Box>
            <Box sx={{ fontSize: 11, letterSpacing: 1, color: textMuted, fontWeight: 700 }}>
              {when.format("MMM").toUpperCase()}
            </Box>
          </Box>
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              flex: "none",
              borderRadius: "10px",
              overflow: "hidden",
              bgcolor: "#000",
            }}
          >
            <Box
              component="img"
              src={show.img_url || placeholderImg}
              alt={show.location?.name || show.title}
              loading="lazy"
              decoding="async"
              sx={{
                display: "block",
                width: "100%",
                height: { xs: 78, sm: 96 },
                objectFit: "cover",
              }}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ color: textMuted, fontSize: 13 }}>
            {when.format("h:mm A")}
            {show.location?.name ? ` · ${show.location.name}` : ""}
            {show.location?.city ? `, ${show.location.city}` : ""}
          </Box>
          <Box sx={{ color: textPrimary, fontWeight: 800, fontSize: { xs: 18, md: 22 }, mt: 0.35, lineHeight: 1.15 }}>
            {show.title}
          </Box>
          {show.slug && (
            <Box
              component={RouterLink}
              to={`/products/${show.slug}`}
              onClick={() => openShow(show)}
              sx={{
                display: "inline-block",
                mt: 0.75,
                color: accent,
                fontWeight: 700,
                fontSize: 14,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Show details
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flex: "none",
            minWidth: { sm: 168 },
          }}
        >
          <Box sx={{ textAlign: { sm: "right" } }}>
            <Box sx={{ color: textPrimary, fontWeight: 800, fontSize: 22 }}>${Math.round(show.ticket_price)}</Box>
            <Box sx={{ color: status.color, fontSize: 13, fontWeight: 700 }}>{status.label}</Box>
          </Box>
          {renderAddControl(show)}
        </Box>
      </Box>
    );
  }

  function renderShowGridCard(show: PublicFestivalShow) {
    const status = statusFor(show);
    const when = dayjsShow(show.start);
    return (
      <Box
        key={show.id}
        id={`show-${show.id}`}
        sx={{
          scrollMarginTop: 88,
          bgcolor: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: "14px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          transition: "border-color 180ms ease",
          "&:hover img": { transform: "scale(1.04)" },
          "&:hover": { borderColor: "rgba(255,255,255,0.28)" },
          ...reducedMotionSx,
        }}
      >
        <Box sx={{ position: "relative", overflow: "hidden", bgcolor: "#000" }}>
          <Box
            component="img"
            src={show.img_url || placeholderImg}
            alt={show.location?.name || show.title}
            loading="lazy"
            decoding="async"
            sx={{
              display: "block",
              width: "100%",
              aspectRatio: "4 / 3",
              objectFit: "cover",
              transition: "transform 280ms ease",
              ...reducedMotionSx,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              px: 1.1,
              py: 0.4,
              borderRadius: 999,
              bgcolor: "rgba(0,0,0,0.72)",
              color: status.color,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {status.label}
          </Box>
        </Box>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 0.75, flex: 1 }}>
          <Box sx={{ color: textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
            {when.format("ddd, MMM D · h:mm A").toUpperCase()}
          </Box>
          <Box sx={{ color: textPrimary, fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>{show.title}</Box>
          {show.location?.name && (
            <Box sx={{ color: "#ddd", fontSize: 14 }}>{show.location.name}</Box>
          )}
          <Box sx={{ mt: "auto", pt: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box sx={{ color: textPrimary, fontWeight: 800, fontSize: 20 }}>${Math.round(show.ticket_price)}</Box>
            {renderAddControl(show)}
          </Box>
          {show.slug && (
            <Box
              component={RouterLink}
              to={`/products/${show.slug}`}
              onClick={() => openShow(show)}
              sx={{ color: accent, fontWeight: 700, fontSize: 14, textDecoration: "none" }}
            >
              Show details
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (tourCopy) {
    const tourShows = [...shows].sort((a, b) => dayjsShow(a.start).valueOf() - dayjsShow(b.start).valueOf());
    const tourIds = new Set(tourShows.map((show) => show.id));
    const lines = festivalCartItems
      .filter((item) => item.show?.id && tourIds.has(item.show.id))
      .map((item) => ({
        id: item.id,
        quantity: item.quantity,
        total_price: item.total_price ?? 0,
        showId: item.show?.id ?? "",
      }));
    return (
      <FoodTourBooking
        festival={festival}
        shows={tourShows}
        lines={lines}
        passTotal={lines.reduce((sum, line) => sum + line.total_price, 0)}
        pendingShowId={pendingShowId}
        samplePreview={samplePreview}
        checkoutDisabled={lines.length === 0 || (isPreview && !samplePreview)}
        quantityFor={quantityFor}
        maxFor={maxQuantityFor}
        onChange={adjustQuantity}
      />
    );
  }

  return (
    <>
      <FestivalPageHelmet
        slug={festival.slug}
        title={festival.title}
        description={festival.description}
        imgUrl={festival.img_url}
        noIndex={!festival.published}
      />
      <Header />

      {samplePreview && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 40,
            bgcolor: accent,
            color: "#000",
            textAlign: "center",
            fontWeight: 700,
            fontSize: 14,
            fontFamily: pageFont,
            py: 1,
            px: 2,
          }}
        >
          Sample layout. These shows are not for sale.
        </Box>
      )}

      {isPreview && !samplePreview && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 40,
            bgcolor: accent,
            color: "#000",
            textAlign: "center",
            fontWeight: 700,
            fontSize: 14,
            fontFamily: pageFont,
            py: 1,
            px: 2,
          }}
        >
          Preview. This festival isn&apos;t published yet, so checkout is turned off.
        </Box>
      )}

      <Box
        sx={{
          bgcolor: pageBg,
          color: textPrimary,
          fontFamily: pageFont,
          minHeight: "100vh",
          pb: { xs: festivalCartItems.length > 0 ? 11 : 0, md: 0 },
        }}
      >
        {tourCopy && <TourFonts />}
        {tourCopy && (
          <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={{ ...publicPageContainerSx, pt: { xs: 4, md: 6 }, pb: { xs: 2, md: 3 } }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr" }, gap: { xs: 3, md: 4 }, alignItems: "start" }}>
              <Box>
                {(dateRangeLabel || cityLabel) && (
                  <Box sx={{ color: accent, fontWeight: 800, letterSpacing: 1.2, fontSize: 13 }}>
                    {[dateRangeLabel, cityLabel].filter(Boolean).join(" · ").toUpperCase()}
                  </Box>
                )}
                <Box component="h1" sx={{ m: 0, mt: 1, fontWeight: 800, fontSize: { xs: 56, md: 80 }, lineHeight: 0.9, letterSpacing: -1.5 }}>
                  {nightCount} night{nightCount === 1 ? "" : "s"}.
                </Box>
                <Box sx={{ fontWeight: 800, fontSize: { xs: 40, md: 64 }, lineHeight: 0.95, color: accent, letterSpacing: -1.2 }}>
                  Neighborhood Classics.
                </Box>
                <Box sx={{ mt: 2, fontSize: 17, lineHeight: 1.45, maxWidth: 460, color: "#ccc" }}>
                  {festival.description || "Live comedy inside the New York classics that make this city. Every ticket supports the shop hosting the show."}
                </Box>
                <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    onClick={() => scrollToId("lineup")}
                    variant="contained"
                    disableElevation
                    sx={{
                      bgcolor: accent,
                      color: "#fff",
                      fontWeight: 800,
                      textTransform: "none",
                      borderRadius: 999,
                      px: 2.5,
                      py: 1.1,
                      "&:hover": { bgcolor: "#ff675e" },
                    }}
                  >
                    Pick your nights
                  </Button>
                  {fromPriceLabel && <Box sx={{ color: textMuted, fontWeight: 700 }}>{fromPriceLabel}</Box>}
                </Box>
              </Box>
              <Box sx={{ bgcolor: "#161616", border: `1px solid ${cardBorder}`, borderRadius: "18px", p: { xs: 1.5, md: 2 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1, px: 0.5 }}>
                  <Box sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.4 }}>QUICK PICK</Box>
                  <Box sx={{ color: textMuted, fontSize: 13 }}>Tap + to add</Box>
                </Box>
                {shows.map((show) => {
                  const when = dayjsShow(show.start);
                  const cast = characterFor(`${show.location?.name ?? ""} ${show.title}`);
                  const status = statusFor(show);
                  return (
                    <Box key={show.id} sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1, borderTop: `1px solid ${hairline}` }}>
                      <Box sx={{ width: 36, flex: "none", textAlign: "center" }}>
                        <Box sx={{ fontSize: 10, letterSpacing: 0.8, color: textMuted, fontWeight: 700 }}>{when.format("ddd").toUpperCase()}</Box>
                        <Box sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>{when.format("D")}</Box>
                      </Box>
                      <Box sx={{ width: 46, height: 52, flex: "none", position: "relative" }}>
                        {cast && (
                          <Box sx={{ position: "absolute", left: "50%", bottom: 0, width: 52, transform: "translateX(-50%)" }}>
                            <ShopDrawing id={cast} alt="" />
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>{show.title}</Box>
                        <Box sx={{ color: textMuted, fontSize: 12, mt: 0.25 }}>
                          {show.location?.name || ""}
                          {Number.isFinite(show.ticket_price) ? ` · $${Math.round(show.ticket_price)}` : ""}
                        </Box>
                      </Box>
                      {availabilityFor(show) === "sold-out" ? (
                        <Box sx={{ color: textMuted, fontSize: 11, fontWeight: 800, letterSpacing: 0.6 }}>SOLD OUT</Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ color: status.color, fontSize: 11, fontWeight: 700, display: { xs: "none", sm: "block" } }}>{status.label}</Box>
                          {renderAddControl(show)}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Container>
        )}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            bgcolor: "rgba(10,10,10,0.94)",
            borderBottom: `1px solid ${hairline}`,
            backdropFilter: "blur(12px)",
          }}
        >
          <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={publicPageContainerSx}>
            <Box
              aria-label="Browse shows"
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 1.25,
                alignItems: { md: "center" },
                py: 1.25,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  flex: 1,
                  minWidth: 0,
                  pb: 0.25,
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-pressed={selectedDay === null && selectedVenue === "all"}
                  onClick={() => {
                    setSelectedDay(null);
                    setSelectedVenue("all");
                    trackFilter("day", "all");
                  }}
                  sx={chipSx(selectedDay === null && selectedVenue === "all")}
                >
                  All shows
                </Box>
                {days.map((day) => (
                  <Box
                    key={day.key}
                    component="button"
                    type="button"
                    aria-pressed={selectedDay === day.key}
                    onClick={() => {
                      setSelectedDay(day.key);
                      trackFilter("day", day.key);
                    }}
                    sx={chipSx(selectedDay === day.key)}
                  >
                    {day.weekday} {day.date}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: "none" }}>
                {venues.length > 1 && (
                  <Box
                    component="select"
                    aria-label="Venue"
                    value={selectedVenue}
                    onChange={(event) => {
                      const value = event.target.value;
                      setSelectedVenue(value);
                      trackFilter("venue", value);
                    }}
                    sx={{
                      fontFamily: tourCopy ? tourBodyFont : pageFont,
                      color: tourCopy ? TOUR_INK : textPrimary,
                      bgcolor: tourCopy ? TOUR_CREAM : "#141414",
                      border: tourCopy ? `2px solid ${TOUR_INK}` : `1px solid ${hairline}`,
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.9,
                      maxWidth: { xs: "46vw", md: 220 },
                    }}
                  >
                    <option value="all">All venues</option>
                    {venues.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Box>
                )}
                {!tourCopy && (
                  <>
                    <Box
                      component="button"
                      type="button"
                      aria-pressed={view === "rows"}
                      onClick={() => {
                        setView("rows");
                        trackFilter("view", "rows");
                      }}
                      sx={chipSx(view === "rows")}
                    >
                      Schedule
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      aria-pressed={view === "grid"}
                      onClick={() => {
                        setView("grid");
                        trackFilter("view", "grid");
                      }}
                      sx={chipSx(view === "grid")}
                    >
                      Grid
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Container>
        </Box>

        <Container
          id="lineup"
          maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH}
          sx={{ ...publicPageContainerSx, pt: { xs: 2, md: 2.5 }, pb: { xs: 6, md: 8 } }}
        >
          <Box sx={{ mb: 1.5, maxWidth: 720 }}>
            {(dateRangeLabel || cityLabel || fromPriceLabel) && (
              <Box sx={{ color: accent, fontWeight: 800, fontSize: 12, letterSpacing: 1.2, mb: 0.75 }}>
                {[dateRangeLabel, cityLabel, fromPriceLabel].filter(Boolean).join(" · ").toUpperCase()}
              </Box>
            )}
            <Box
              component={tourCopy ? "h2" : "h1"}
              sx={{
                m: 0,
                fontFamily: tourCopy ? tourDisplayFont : pageFont,
                fontWeight: 800,
                fontSize: { xs: tourCopy ? 36 : 28, md: tourCopy ? 48 : 36 },
                lineHeight: 1,
                letterSpacing: tourCopy ? 0 : -0.8,
              }}
            >
              {tourCopy ? "Choose your shows" : festival.title}
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: tourCopy ? "minmax(0, 1fr) 320px" : "minmax(0, 1fr) 280px" },
              gap: { xs: 2, md: 2.5 },
              alignItems: "start",
            }}
          >
            <Box sx={{ minWidth: 0 }}>

              {visibleShows.length === 0 ? (
                <Box sx={{ color: textMuted, py: 8, textAlign: "center", borderTop: `1px solid ${hairline}` }}>
                  No shows match this filter.
                </Box>
              ) : view === "grid" ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  {visibleShows.map(renderShowGridCard)}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {dayGroups.map((group) => (
                    <Box key={group.key}>
                      <Box sx={{ color: textMuted, fontSize: 13, fontWeight: 800, letterSpacing: 0.6, mb: 1.25 }}>
                        {group.label.toUpperCase()}
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                        {group.shows.map(renderShowRow)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: { xs: tourCopy ? "block" : "none", md: "block" },
                position: "sticky",
                top: 72,
                bgcolor: cardBg,
                color: textPrimary,
                border: `1px solid ${cardBorder}`,
                borderRadius: "14px",
                p: 2,
                fontFamily: pageFont,
              }}
            >
              <Box sx={{ color: textMuted, fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
                {tourCopy ? "YOUR NIGHTS" : "YOUR PASS"}
              </Box>
              <Box sx={{ fontWeight: 900, fontSize: 32, mt: 0.5, lineHeight: 1.1 }}>
                {festivalCartItems.length}{" "}
                <Box component="span" sx={{ fontSize: 15, fontWeight: 600, color: "#ccc" }}>
                  {festivalCartItems.length === 1 ? "show" : "shows"}
                </Box>
              </Box>
              <Box sx={{ borderTop: `1px solid ${hairline}`, mt: 2, pt: 2 }}>
                {festivalCartItems.length === 0 ? (
                  <Box sx={{ color: textMuted, fontSize: 14, py: 2 }}>
                    {tourCopy ? "Nothing selected yet. Add a night and it shows up here." : "Nothing selected yet. Add a show to build your night."}
                  </Box>
                ) : tourCopy ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {festivalCartItems.map((item) => {
                      const matched = shows.find((show) => show.id === item.show?.id);
                      const cast = characterFor(`${matched?.location?.name ?? ""} ${matched?.title ?? item.show?.title ?? ""}`);
                      const when = matched ? dayjsShow(matched.start) : null;
                      const place = [matched?.location?.name, matched?.location?.city].filter(Boolean).join(", ");
                      return (
                        <Box key={item.id} sx={{ position: "relative", mt: cast ? 3.5 : 0, borderRadius: "12px", overflow: "visible", border: `1px solid ${cardBorder}`, bgcolor: "#101010" }}>
                          {cast && (
                            <Box sx={{ position: "absolute", top: -36, right: 12, width: 34, height: 44, pointerEvents: "none" }}>
                              <ShopDrawing id={cast} alt="" celebrate fill />
                            </Box>
                          )}
                          <Box sx={{ height: 92, borderRadius: "12px 12px 0 0", overflow: "hidden", bgcolor: "#000" }}>
                            <Box
                              component="img"
                              src={matched?.img_url || placeholderImg}
                              alt={matched?.location?.name || item.show?.title || ""}
                              sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          </Box>
                          <Box sx={{ p: 1.25 }}>
                            <Box sx={{ color: textMuted, fontSize: 12, fontWeight: 700 }}>
                              {when ? `${when.format("ddd").toUpperCase()} ${when.format("D")} ${when.format("MMM").toUpperCase()} · ${when.format("h:mm A")}` : ""}
                            </Box>
                            {place && <Box sx={{ fontSize: 13, mt: 0.35 }}>{place}</Box>}
                            <Box sx={{ fontWeight: 800, fontSize: 15, mt: 0.35, lineHeight: 1.2 }}>{item.show?.title}</Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75, color: textMuted, fontSize: 13 }}>
                              <Box>{item.quantity} {item.quantity === 1 ? "ticket" : "tickets"}</Box>
                              <Box sx={{ color: textPrimary, fontWeight: 800 }}>${Math.round(item.total_price ?? 0)}</Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {festivalCartItems.map((item) => (
                      <Box key={item.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1, fontSize: 14 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.show?.title}
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, color: textMuted, fontSize: 12 }}>
                            <ConfirmationNumberOutlinedIcon sx={{ fontSize: 13 }} />
                            {item.quantity}
                          </Box>
                        </Box>
                        <Box sx={{ fontWeight: 700, flex: "none" }}>${Math.round(item.total_price ?? 0)}</Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Box sx={{ borderTop: `1px solid ${hairline}`, mt: 2, pt: 2, display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ color: textMuted, fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>TOTAL</Box>
                <Box sx={{ fontWeight: 900, fontSize: 22 }}>${Math.round(passTotal)}</Box>
              </Box>
              <Button
                fullWidth
                disabled={festivalCartItems.length === 0 || (isPreview && !samplePreview)}
                href={samplePreview ? undefined : "/cart"}
                onClick={samplePreview ? (event) => event.preventDefault() : undefined}
                variant="contained"
                disableElevation
                sx={{
                  mt: 2,
                  borderRadius: 999,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 700,
                  fontFamily: pageFont,
                  bgcolor: festivalCartItems.length === 0 ? "rgba(255,255,255,0.12) !important" : accent,
                  color: festivalCartItems.length === 0 ? "rgba(255,255,255,0.45) !important" : "#fff",
                  "&:hover": { bgcolor: "#ff675e" },
                }}
              >
                {festivalCartItems.length === 0 ? "Select shows to continue" : "Continue to checkout"}
              </Button>
            </Box>
          </Box>
        </Container>

        {tourCopy && (
          <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={{ ...publicPageContainerSx, pb: { xs: 6, md: 8 } }}>
            <Box
              sx={{
                borderTop: `1px solid ${hairline}`,
                pt: { xs: 5, md: 7 },
              }}
            >
              <Box sx={{ color: "#ccc", fontSize: { xs: 16, md: 18 }, lineHeight: 1.55, maxWidth: 640 }}>
                We&apos;re bringing stand-up comedy into the small businesses that make New York, New York. From legendary neighborhood institutions to unexpected local favorites, every stop transforms an iconic NYC space into a one-night-only comedy experience.
              </Box>
            </Box>
          </Container>
        )}

        {!tourCopy && venueCards.length > 0 && (
          <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={{ ...publicPageContainerSx, pb: { xs: 6, md: 8 } }}>
            <Box sx={{ color: textMuted, fontSize: 12, fontWeight: 800, letterSpacing: 1.5, mb: 1 }}>THE VENUES</Box>
            <Box component="h2" sx={{ m: 0, mb: 2.5, fontSize: { xs: 28, md: 36 }, fontWeight: 900, letterSpacing: -0.6 }}>
              {tourCopy ? "The businesses hosting the night." : "Where the shows happen."}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
                gap: 1.5,
              }}
            >
              {venueCards.map(({ name, show }) => (
                <Box
                  key={name}
                  component="button"
                  type="button"
                  onClick={() => {
                    setSelectedVenue(name);
                    setSelectedDay(null);
                    setView("rows");
                    trackFilter("venue", name);
                    scrollToId("lineup");
                  }}
                  sx={{
                    p: 0,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: "14px",
                    overflow: "hidden",
                    bgcolor: cardBg,
                    color: "inherit",
                    fontFamily: pageFont,
                    textAlign: "left",
                    cursor: "pointer",
                    "&:hover img": { transform: "scale(1.04)" },
                    ...reducedMotionSx,
                  }}
                >
                  <Box
                    component="img"
                    src={show.img_url || placeholderImg}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    sx={{
                      display: "block",
                      width: "100%",
                      height: { xs: 110, md: 150 },
                      objectFit: "cover",
                      transition: "transform 280ms ease",
                      ...reducedMotionSx,
                    }}
                  />
                  <Box sx={{ px: 1.5, py: 1.25, fontWeight: 800, fontSize: 14 }}>{name}</Box>
                </Box>
              ))}
            </Box>
          </Container>
        )}

        <Box sx={{ borderTop: `1px solid ${hairline}` }}>
          <Container maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH} sx={{ ...publicPageContainerSx, py: { xs: 6, md: 9 } }}>
            <Box component="h2" sx={{ m: 0, fontFamily: tourCopy ? tourDisplayFont : pageFont, fontSize: { xs: 34, md: 52 }, fontWeight: 800, letterSpacing: tourCopy ? 0 : -1.2, maxWidth: 640, lineHeight: 0.98 }}>
              {tourCopy ? "Build your tour." : "Pick a night. Take a seat."}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 3 }}>
              <Button
                variant="contained"
                disableElevation
                onClick={() => scrollToId("lineup")}
                sx={{
                  bgcolor: tourCopy ? TOUR_POP : accent,
                  color: "#fff",
                  borderRadius: 999,
                  px: 2.75,
                  py: 1.2,
                  fontWeight: 700,
                  fontFamily: tourCopy ? tourBodyFont : pageFont,
                  textTransform: "none",
                  border: tourCopy ? `2px solid ${TOUR_INK}` : "none",
                  "&:hover": { bgcolor: tourCopy ? "#c94e30" : "#ff675e" },
                }}
              >
                Explore the shows
              </Button>
              <Button
                component={RouterLink}
                to="/waitlist"
                onClick={() => trackWaitlistClick()}
                variant="outlined"
                sx={{
                  color: tourCopy ? TOUR_INK : "#fff",
                  borderColor: tourCopy ? TOUR_INK : "rgba(255,255,255,0.4)",
                  borderRadius: 999,
                  px: 2.75,
                  py: 1.2,
                  fontWeight: 700,
                  fontFamily: pageFont,
                  textTransform: "none",
                  "&:hover": { borderColor: "#fff", bgcolor: "transparent" },
                }}
              >
                Join the waitlist
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      {festivalCartItems.length > 0 && (
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 30,
            bgcolor: tourCopy ? TOUR_CREAM : "#111",
            color: tourCopy ? TOUR_INK : textPrimary,
            borderTop: tourCopy ? `2px solid ${TOUR_INK}` : `1px solid ${hairline}`,
            px: 2,
            py: 1.25,
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            fontFamily: pageFont,
          }}
        >
          <Box>
            <Box sx={{ fontWeight: 800 }}>
              {festivalCartItems.length} {festivalCartItems.length === 1 ? "show" : "shows"}
            </Box>
            <Box sx={{ color: textMuted, fontSize: 13 }}>${Math.round(passTotal)}</Box>
          </Box>
          <Button
            href={samplePreview ? undefined : "/cart"}
            disabled={isPreview && !samplePreview}
            onClick={samplePreview ? (event) => event.preventDefault() : undefined}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: accent,
              color: "#fff",
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              fontFamily: pageFont,
              px: 2.5,
              "&:hover": { bgcolor: "#ff675e" },
            }}
          >
            Checkout
          </Button>
        </Box>
      )}

      <Footer />

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
